// Contrato de la pantalla Control MDM: tipos de UI sobre el contrato publicado en @fiado/type-kit.
// Sobrevive al backend: el mapper de `api/mappers.ts` traduce el wire del silo a estos tipos.
import type { LoanDeviceLockReasonEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanDeviceLockReasonEnum';
import type { LoanDeviceMdmStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanDeviceMdmStatusEnum';
import type { MdmDeviceStatusEnum } from '@fiado/type-kit/bin/mdm/enums/MdmDeviceStatusEnum';
import type { MdmOperationStatusEnum } from '@fiado/type-kit/bin/mdm/enums/MdmOperationStatusEnum';
import type { MdmOperationTypeEnum } from '@fiado/type-kit/bin/mdm/enums/MdmOperationTypeEnum';
import type { MdmProviderEnum } from '@fiado/type-kit/bin/mdm/enums/MdmProviderEnum';
import type { MdmLockModeEnum } from '@fiado/type-kit/bin/retailCatalog/enums/MdmLockModeEnum';
import type { LoanDeviceFleetError, LoanDeviceOperationItem } from '@/api/services/loan-credit';
import type {
    LoanDeviceRefreshReview,
    MdmRefreshOutcome,
    MdmRefreshReason,
} from '@/api/services/loan-credit/contract';
import type { components } from '@/api/services/loan-credit/loan-credit.gen';
import type { MdmFleetState, MdmLockPhase } from './logic';

/** Algo del equipo que necesita a una persona, tal como lo deriva el expediente. */
export type MdmDeviceAlert = components['schemas']['MdmDeviceAlert'];

export type MdmDeviceAlertCode = MdmDeviceAlert['code'];

/** Último fallo devuelto por el proveedor; el catálogo de códigos es el del OpenAPI, más amplio que type-kit. */
export interface MdmDeviceError {
    code: LoanDeviceFleetError['code'];
    message?: string;
    at?: string;
}

/** Una fila del fleet: el expediente del equipo de un crédito, alineado a LoanDeviceStatusResult. */
export interface MdmFleetDevice {
    creditId: string;
    imei: string;
    /** Estado NUESTRO del expediente: es el autoritativo y el que manda en la UI. */
    mdmStatus: LoanDeviceMdmStatusEnum;
    mdmProvider: MdmProviderEnum;
    mdmLockMode?: MdmLockModeEnum;
    /** El proveedor dio la operación por hecha. En `false` el estado es una inferencia nuestra. */
    mdmConfirmed: boolean;
    /** Cuándo NUESTRO sistema dio el candado por confirmado; no es la hora en que cayó en el equipo. */
    mdmConfirmedAt?: string;
    /** Dígitos del código que muestra el equipo bloqueado. Ausente si el proveedor no lo declara. */
    devicePasskeyLength?: number;
    /** Lo que necesita a una persona. El mapper siempre lo llena; ausente se lee como vacío. */
    mdmAlerts?: readonly MdmDeviceAlert[];
    /** El proveedor desmintió el candado: el equipo quedó sin candado. */
    lockRefutedByProvider: boolean;
    /**
     * ⚠️ Marca, modelo y cliente NO viven en el expediente MDM: el silo solo conoce el IMEI, el
     * crédito y el SKU. Vienen ausentes desde el backend y la pantalla pinta «—».
     */
    brand?: string;
    model?: string;
    /** SKU del producto financiado, congelado al enrolar. Es lo único que el expediente sabe del equipo. */
    productSku?: string;
    customerId?: string;
    customerName?: string;
    /** Referencia legible del crédito. Sin folio en el expediente, es el propio `creditId`. */
    creditRef: string;
    lockReason?: LoanDeviceLockReasonEnum;
    lockedAt?: string;
    currentLockScheduledFor?: string;
    /** Cuándo ordenamos el último desbloqueo; un corte que no es posterior es de otro episodio. */
    unlockedAt?: string;
    lastSeenAt?: string;
    enrolledAt?: string;
    lastOperationType?: MdmOperationTypeEnum;
    lastOperationAt?: string;
    /** Última escritura del expediente; decide qué copia manda si un equipo llega repetido. */
    updatedAt?: string;
    /** Operaciones esperando respuesta del proveedor (columna «Ops»). */
    pendingOperations: number;
    /** Referencia de la operación en vuelo; con ella soporte la busca en la tabla del connector. */
    operationReference?: string;
    /** Folio opaco del proveedor para esa operación: solo trazabilidad, no se consulta con él. */
    pollingHandle?: string;
    lastError?: MdmDeviceError;
}

/**
 * El veredicto de UNA consulta al proveedor, tal como lo pinta la ficha.
 *
 * No es un desenlace que se anuncia y se tira: `outcome` dice qué se pudo afirmar y `changed` si el
 * expediente se movió, que es lo que el operador viene a ver cuando aprieta «Actualizar».
 */
export interface MdmRefreshReview {
    outcome: MdmRefreshOutcome;
    /** Cuándo se le preguntó al proveedor; alimenta el «Actualizado hace X». */
    observedAt: string;
    /** Por qué no se afirmó nada. Sólo llega con `NO_EVIDENCE`. */
    reason?: MdmRefreshReason;
    /** Cómo ve el proveedor al equipo. Ausente si no contestó. */
    providerStatus?: MdmDeviceStatusEnum;
    /** Con qué falló el proveedor. Sólo con `PROVIDER_SILENT`. */
    providerErrorCode?: LoanDeviceRefreshReview['providerErrorCode'];
    /** Si el expediente cambió con esta consulta. */
    changed: boolean;
}

/** Antigüedad del último heartbeat; `not-reported` = el proveedor no reporta señal. */
export type HeartbeatBucket =
    | 'up-to-date'
    | 'recent'
    | 'stale'
    | 'critical'
    | 'unknown'
    | 'not-reported';

export type HeartbeatSeverity = 'ok' | 'warn' | 'danger' | 'neutral';

export interface HeartbeatReading {
    bucket: HeartbeatBucket;
    severity: HeartbeatSeverity;
    /** Forma corta para la columna angosta de la tabla: «Hoy», «7d». */
    label: string;
    /** Forma larga para la ficha, donde hay lugar: «Hoy», «hace 7 días». */
    longLabel: string;
    /** Días completos desde el último heartbeat; `undefined` cuando nunca reportó. */
    daysSinceLastSeen?: number;
}

/** Valor de filtro que no filtra nada. */
export const ANY_FILTER = 'all';
export type AnyFilter = typeof ANY_FILTER;

/** Equipos por página de la tabla. La columna del explorer es angosta: entran menos que en Cartera. */
export const MDM_FLEET_PAGE_SIZE = 25;

export interface MdmFleetFilters {
    search: string;
    /** El estado de `readFleetState`: el mismo que pinta la fila y cuenta el KPI. */
    state: MdmFleetState | AnyFilter;
    /** `needed` deja solo lo que cuenta el KPI «Atención». */
    attention: 'needed' | AnyFilter;
    /** Detalle de implementación nuestro: filtra, pero nunca es el eje de la pantalla. */
    provider: MdmProviderEnum | AnyFilter;
    heartbeat: HeartbeatBucket | AnyFilter;
    /** `ANY_FILTER` deja pasar todas las marcas. */
    brand: string;
}

/** Los filtros sin tocar: la flota entera. */
export const EMPTY_FLEET_FILTERS: MdmFleetFilters = {
    search: '',
    state: ANY_FILTER,
    attention: ANY_FILTER,
    provider: ANY_FILTER,
    heartbeat: ANY_FILTER,
    brand: ANY_FILTER,
};

export type MdmStatusTone = 'success' | 'danger' | 'warning' | 'neutral' | 'info';

export interface MdmStatusPresentation {
    label: string;
    /** Versión corta para la columna «Estado»; la larga viaja en el tooltip de la celda. */
    shortLabel: string;
    tone: MdmStatusTone;
    /** Prende la señal de «esto lo tiene que ver una persona». */
    needsAttention: boolean;
}

/**
 * La acción que la ficha resalta según el estado; `null` cuando no hay razón verificable para
 * sugerir nada. Ninguna destructiva entra aquí.
 */
export type MdmSuggestedAction = 'unlock' | null;

/**
 * Qué le pasa a este equipo y qué conviene hacer, con el porqué. El encabezado se pinta siempre;
 * `suggested` en `null` solo le quita la línea de la acción sugerida.
 */
export interface MdmDeviceSituation {
    headline: string;
    suggested: MdmSuggestedAction;
    reason: string;
    tone: MdmStatusTone;
}

/** Una llamada al proveedor, tal como la guarda el audit log de operaciones. */
export interface MdmAuditEntry {
    callId: string;
    creditId: string;
    imei: string;
    provider: MdmProviderEnum;
    operationType: MdmOperationTypeEnum;
    /** Cómo terminó la llamada. La tabla no guarda latencia, código HTTP ni reintentos. */
    status: MdmOperationStatusEnum;
    /** El código tal como lo publica el OpenAPI: trae valores que el enum de type-kit no tiene. */
    errorCode?: LoanDeviceOperationItem['errorCode'];
    /** Motivo en texto humano, tal como lo manda el proveedor a través del lambda. */
    errorMessage?: string;
    at: string;
}

/**
 * Foto de la flota que se pinta en la barra de KPIs. Todo sale del listado que la pantalla ya trae:
 * no hay endpoint de agregados y estas cuentas NO son la salud del connector, que sigue sin fuente.
 */
export interface MdmFleetSummary {
    total: number;
    byProvider: Record<MdmProviderEnum, number>;
    /** Solo ACTIVE confirmados y sin corte vencido: lo demás no es un equipo libre. */
    active: number;
    /** ACTIVE que el proveedor todavía no confirma. */
    activeUnconfirmed: number;
    /** Solo candados confirmados (fase `locked`): una orden no es un equipo bloqueado. */
    locked: number;
    /** Lo que no es candado confirmado ni equipo libre, por fase (incluye el corte vencido). */
    lockOrdersByPhase: Record<Exclude<MdmLockPhase, 'locked'>, number>;
    enrolling: number;
    /** Equipos con alertas vivas o con el enrolamiento trabado (`needsPersonAttention`). */
    needsAttention: number;
    /** Equipos cuyo último heartbeat pasa de siete días. */
    silent: number;
    /** Equipos sin un solo heartbeat registrado: nunca es lo mismo que llevar días callado. */
    neverReported: number;
    /** Operaciones esperando respuesta del proveedor, sumadas sobre toda la flota. */
    pendingOperations: number;
    /** En cuántos equipos están repartidas esas operaciones. */
    devicesWithPendingOperations: number;
}
